/**
 * AIUsagePanel - how much AI one item has used, and what it used it on.
 *
 * ONE panel for every entity that runs AI. A matter, a table, a draft and a
 * review session all answer the same three questions - how much AI did this
 * use, which kinds of run used it, and who ran them - so they get one
 * component parameterised by `entityType` + `entityId` rather than four
 * near-copies that would drift the way the audit log's copies did.
 *
 * The three sections, in the order the questions are asked:
 *
 *   1. TOTALS   the headline: tokens, runs.
 *   2. BREAKDOWN by run type, heaviest first - "what is it using AI on".
 *   3. RUNS      newest first, each expandable into its own calls.
 *
 * ── NEVER OUR COST. CUSTOMER VALUE ON THE DEV RESOURCE ENV ONLY. ───────
 * THIS PANEL IS CUSTOMER-FACING on every one of its surfaces. Our per-token
 * provider cost (`cost_usd`) is our economics and is NEVER drawn here —
 * `aiUsageModel` drops it at the boundary, so a backend that keeps sending it
 * cannot surface it.
 *
 * What CAN appear - and only on the dev resource environment (dev.pons.io /
 * demo.pons.io) - is the customer's OWN value: `units × our list price per
 * unit`, the same figure the allowance block shows. It is gated server-side:
 * `/payments/packs/allowance-usage` returns `cost_display:false` and omits all
 * prices on production, so a real customer never sees a euro figure and the
 * panel stays usage-only in prod exactly as before. The value line below draws
 * nothing unless the server sent prices, so its absence is the production
 * default, not a client decision.
 *
 * A run's user_id is resolved to a PERSON through the org directory and drawn
 * with `PersonCell`, the same face-and-name the access panes and every table
 * row use. A raw id is never rendered: "who ran the analysis" is the
 * question, and `a3f1…` is not an answer.
 *
 * NO CHROME OF ITS OWN inside a pane. `Panel` already draws the glass box,
 * the header and the scroll region (see `shared/surface/README.md`), so the
 * title bar and the refresh button move into the pane's header and footer —
 * exactly what `AuditLogPanel` does, and for the same reason.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiArrowsClockwiseLight,
  PiCaretRightLight,
  PiCpuLight,
  PiListBulletsLight,
  PiSparkleLight,
  PiWarningCircleLight,
  PiXLight,
} from 'react-icons/pi';
import Button from '../TailwindComponents/Button';
import Badge from '../TailwindComponents/Badge';
import Spinner from '../TailwindComponents/Spinner';
import Skeleton from '../TailwindComponents/Skeleton';
import GlassCard from '../brand/GlassCard';
import { InfoRow, KpiTile, Well } from '../brand/Field';
import PersonCell from '../access/PersonCell';
import PaneFooter from '../surface/PaneFooter';
import { useInPane } from '../surface/PaneBody';
import { feedTimeLabel } from '../feed/feedTime';
import { useOrgMembers } from '../../../hooks/useOrgMembers';
import { useAppDispatch } from '../../../store';
import { AllowanceSummary, fetchUnitBalances } from '../../../store/actions/PackActions';
import { AIUsageEntityType, AIUsageRun } from './aiUsageModel';
import { useAIUsage, useAIUsageRunCalls } from './useAIUsage';
import { isAiUsageEnabled } from '../../../config/featureFlags';
import {
  formatBytes,
  formatDuration,
  formatModelMix,
  formatTokens,
  formatTokensCompact,
  humaniseRunType,
  runTypeLabelKey,
} from './aiUsageFormat';

export interface AIUsagePanelProps {
  entityType: AIUsageEntityType;
  entityId: string;
  /** Shown under the title when the panel draws its own header. */
  entityName?: string;
  /** Closes the host. Only reachable from the panel's own header. */
  onClose?: () => void;
  /**
   * Drop the panel's own title bar. Automatic inside a surface pane - the
   * pane has a header and a close button already, and two stacked read as a
   * bug. Explicit values win, for a host that has a header without being a
   * pane.
   */
  hideHeader?: boolean;
}

/** Failed runs are the ones worth spotting in a list; the rest stay quiet. */
const statusTone = (status: string): 'gray' | 'red' | 'yellow' => {
  const value = (status || '').toLowerCase();
  if (value === 'failed' || value === 'error') return 'red';
  if (value === 'running' || value === 'queued' || value === 'processing') return 'yellow';
  return 'gray';
};

const AIUsagePanelBody: React.FC<AIUsagePanelProps> = ({
  entityType,
  entityId,
  entityName,
  onClose,
  hideHeader,
}) => {
  const { t } = useTranslation();
  const inPane = useInPane();
  const withoutHeader = hideHeader ?? inPane;

  const { usage, loading, error, refresh } = useAIUsage(entityType, entityId);
  const { callsByRun, loadingRunId, loadCalls } = useAIUsageRunCalls();
  const { members } = useOrgMembers();
  const dispatch = useAppDispatch();
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);

  /**
   * Pricing is fetched once and used ONLY to value run-type rows. It carries
   * our list price per unit and the run_type→unit_type map, and `cost_display`
   * is true only on the dev resource environment - so on production this stays
   * null-of-prices and no euro figure is ever drawn (see the header note).
   */
  const [pricing, setPricing] = useState<AllowanceSummary | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await dispatch(fetchUnitBalances() as any);
      if (!cancelled) setPricing((result as AllowanceSummary) || null);
    })();
    return () => { cancelled = true; };
  }, [dispatch]);

  /** Euro, tax-exclusive. Only reached when the server sent prices (dev env). */
  const eur = (value: number): string => {
    const currency = pricing?.currency || 'EUR';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
      }).format(value);
    } catch {
      return `€${value.toFixed(2)}`;
    }
  };

  /**
   * The customer-value of a run-type's runs, or null when it can't/shouldn't be
   * priced: no pricing, cost hidden (prod), or an unmetered/`ask` run type the
   * server left out of the price map. Value = runs × our list price per unit.
   */
  const valueForRunType = (runType: string, runs: number): number | null => {
    if (!pricing?.cost_display || !pricing.unit_prices || !pricing.unit_type_by_run_type) return null;
    const unit = pricing.unit_type_by_run_type[runType];
    const price = unit ? pricing.unit_prices[unit] : undefined;
    if (price == null) return null;
    return price * (runs || 0);
  };

  /**
   * user_uuid → the person, so a run row can draw a face instead of an id.
   *
   * `useOrgMembers` IS the uuid-keyed directory: `/org-people/people` answers
   * with the decrypted user uuid in `id` (see the org people service), which
   * is the same handle the metering tables now record on every run. Lowercased
   * on both sides because a uuid is case-insensitive and the two services do
   * not have to agree on which case they store it in.
   */
  const peopleByUuid = useMemo(() => {
    const map = new Map<string, any>();
    members.forEach((member) => {
      const uuid = String(member.id || '').trim().toLowerCase();
      if (uuid) map.set(uuid, member);
    });
    return map;
  }, [members]);

  /** The person behind a run, or null - which PersonCell draws as an em-dash. */
  const personFor = (userUuid: string): any =>
    peopleByUuid.get(String(userUuid || '').trim().toLowerCase()) || null;

  /**
   * The friendly run-type name.
   *
   * Mapped types go through `t()`; unmapped ones are humanised and rendered
   * as-is - see the note in `aiUsageFormat`, which is also why this cannot be
   * one `t()` call with a fallback.
   */
  const runTypeName = (runType: string): string => {
    const key = runTypeLabelKey(runType);
    return key ? t(key) : humaniseRunType(runType) || t('Unnamed run');
  };

  const toggleRun = (runId: string) => {
    if (expandedRunId === runId) {
      setExpandedRunId(null);
      return;
    }
    setExpandedRunId(runId);
    loadCalls(runId);
  };

  /*
    BELT AND BRACES on the two lists the pane maps over. `normaliseUsage`
    already guarantees arrays for any payload - but it is the only thing
    that does, and a pane docked in a workspace takes the whole page down
    with it when it throws. A 500, a half-written response or a contract
    that moves should land the reader in the empty state, not in an error
    boundary. Cheap here, and the failure it prevents is not.
  */
  const totals = usage?.totals;
  const runs = Array.isArray(usage?.runs) ? usage!.runs : [];
  const breakdown = Array.isArray(usage?.breakdown) ? usage!.breakdown : [];

  /**
   * THE ROLLED-UP TOTAL - a matter's own runs plus everything linked to it.
   *
   * Drawn only when all three hold:
   *
   *   1. This is a MATTER. Only a matter's response carries
   *      `totals_including_linked`, and the line's own wording ("including
   *      linked drafts, tables & reviews") is a claim about a matter's work.
   *      On a table it would be both unsourced and untrue.
   *   2. The backend actually sent it. Absent → the line is simply not
   *      there; no error, no zero, no placeholder.
   *   3. It is BIGGER than the matter's own figure. A matter with nothing
   *      linked reports the same number twice, and a second line repeating
   *      the headline underneath it is noise pretending to be information.
   */
  const linkedTotals = usage?.totalsIncludingLinked ?? null;
  const showLinkedTotals =
    entityType === 'matter'
    && !!linkedTotals
    && !!totals
    && linkedTotals.total > totals.total;

  /* ── the body ──────────────────────────────────────────────────── */

  let body: React.ReactNode;

  if (loading && !usage) {
    // Content-shaped, not a spinner: the strip and the list are what lands.
    body = <Skeleton variant="list" rows={4} inset="none" />;
  } else if (error) {
    body = (
      <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
        <PiWarningCircleLight className="h-7 w-7 text-danger-500" />
        <p className="text-sm text-fg-body">{t('Could not load AI usage')}</p>
        {/* `useAIUsage` guarantees a string; this is the second lock on the
            same door. Rendering a non-string child here is what crashed the
            app when the endpoint 500'd with `{ title, description }`, and a
            detail line is not worth that risk - an unexpected shape simply
            shows no detail under the headline. */}
        {typeof error === 'string' && !!error.trim() && (
          <p className="max-w-xs text-xs text-fg-muted">{error}</p>
        )}
        <Button variant="outline" size="small" onClick={refresh}>{t('Try again')}</Button>
      </div>
    );
  } else if (!runs.length && !(totals?.runs)) {
    /*
      THE EMPTY STATE, and it is the common one. Most items will never have
      run AI, and an item that has not is not in an error - so this reads as
      a fact about the item rather than as something that went wrong.
    */
    body = (
      <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
        <PiSparkleLight className="h-8 w-8 text-fg-muted" />
        <p className="text-sm text-fg-body">{t('No AI usage yet')}</p>
        <p className="max-w-xs text-xs text-fg-muted">
          {t('Token figures appear here once an AI run finishes for this item.')}
        </p>
      </div>
    );
  } else {
    body = (
      <div className="space-y-4">
        {/* ── 1. TOTALS ───────────────────────────────────────────── */}
        <GlassCard padding="none">
          {/* Two equal tiles - consumption on the left, activity on the
              right. Both are usage facts, which is the whole headline: how
              much was used, and over how many runs. */}
          <div className="grid grid-cols-2 divide-x divide-hairline-soft">
            <KpiTile
              icon={<PiCpuLight />}
              value={formatTokens(totals?.total ?? 0)}
              label={t('Total tokens')}
              hint={t('{{n}} cached', { n: formatTokensCompact(totals?.cached ?? 0) })}
            />
            <KpiTile
              icon={<PiSparkleLight />}
              value={formatTokens(totals?.runs ?? runs.length)}
              label={t('Total runs')}
              hint={t('{{n}} tokens', { n: formatTokensCompact(totals?.total ?? 0) })}
            />
          </div>

          {/*
            SECONDARY, and it has to stay that way. The headline is what THIS
            matter's own runs used - the figure someone is accountable for —
            and the roll-up is context beside it. So it is one quiet line
            under the strip rather than a third tile, which would read as a
            third equal fact and put the bigger number in the same weight as
            the direct one.
          */}
          {showLinkedTotals && linkedTotals && (
            <div
              className="flex items-center justify-between gap-3 border-t
                         border-hairline-soft px-4 py-2"
              data-testid="ai-usage-linked-total"
            >
              <span className="min-w-0 truncate text-[11px] text-fg-muted">
                {t('Including linked drafts, tables & reviews')}
              </span>
              <span className="flex-shrink-0 text-xs tabular-nums text-fg-body">
                {t('{{n}} tokens', { n: formatTokensCompact(linkedTotals.total) })}
                {' · '}
                {t('{{n}} runs', { n: linkedTotals.runs })}
              </span>
            </div>
          )}
        </GlassCard>

        {/* The token split, quiet: it explains the headline without competing
            with it, so it is rows in a card rather than four more tiles. */}
        {!!totals && (
          <GlassCard title={t('Tokens')} icon={<PiCpuLight />}>
            <div className="space-y-2">
              <InfoRow label={t('Prompt')} value={formatTokens(totals.prompt)} />
              <InfoRow label={t('Cached')} value={formatTokens(totals.cached)} />
              <InfoRow label={t('Completion')} value={formatTokens(totals.completion)} />
              {totals.reasoning > 0 && (
                <InfoRow label={t('Reasoning')} value={formatTokens(totals.reasoning)} />
              )}
            </div>
          </GlassCard>
        )}

        {/* ── 2. BREAKDOWN BY RUN TYPE ────────────────────────────── */}
        {!!breakdown.length && (
          <GlassCard title={t('By run type')} icon={<PiSparkleLight />}>
            <div className="space-y-2">
              {breakdown.map((row) => {
                const rowValue = valueForRunType(row.runType, row.runs);
                return (
                <Well key={row.runType} className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-fg-body" title={row.runType}>
                      {runTypeName(row.runType)}
                    </p>
                    <p className="text-[11px] text-fg-muted tabular-nums">
                      {t('{{n}} runs', { n: row.runs })}
                      {' · '}
                      {t('{{n}} tokens', { n: formatTokens(row.totalTokens) })}
                    </p>
                  </div>
                  {/* Tokens are the figure the rows are sorted by, so they sit
                      on the row's right edge; the customer-value (dev env only)
                      rides quietly beneath. */}
                  <span className="flex flex-shrink-0 flex-col items-end leading-tight">
                    <span className="text-sm font-medium tabular-nums text-fg-strong">
                      {formatTokensCompact(row.totalTokens)}
                    </span>
                    {rowValue != null && (
                      <span className="text-[11px] tabular-nums text-champagne">
                        {eur(rowValue)}
                      </span>
                    )}
                  </span>
                </Well>
                );
              })}
            </div>
          </GlassCard>
        )}

        {/* ── 3. THE RUNS ─────────────────────────────────────────── */}
        {!!runs.length && (
          <GlassCard title={t('Runs')} icon={<PiListBulletsLight />}>
            <div className="space-y-2">
              {runs.map((run) => (
                <RunRow
                  key={run.id || `${run.runType}-${run.startedAt}`}
                  run={run}
                  name={runTypeName(run.runType)}
                  person={personFor(run.userUuid)}
                  expanded={expandedRunId === run.id}
                  calls={callsByRun[run.id]}
                  callsLoading={loadingRunId === run.id}
                  onToggle={() => toggleRun(run.id)}
                />
              ))}
            </div>
          </GlassCard>
        )}
      </div>
    );
  }

  return (
    <>
      {!withoutHeader && (
        <div className="flex flex-shrink-0 items-center justify-between border-b border-hairline-soft px-5 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <PiSparkleLight className="h-5 w-5 flex-shrink-0 text-fg-muted" />
              <h3 className="truncate text-sm text-fg-strong">{t('AI usage')}</h3>
            </div>
            {entityName && (
              <p className="mt-0.5 truncate text-xs text-fg-muted">{entityName}</p>
            )}
          </div>
          {onClose && (
            <Button variant="neutral" size="small" onClick={onClose} ariaLabel={t('Close')}>
              <PiXLight className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main px-4 py-4">
        {body}
      </div>

      {inPane && (
        <PaneFooter
          /* The pane's own stat: the headline figure, so the number someone
             opened the pane for is on its bottom edge too. */
          stat={
            <span className="tabular-nums">
              {t('{{n}} tokens', { n: formatTokensCompact(totals?.total ?? 0) })}
              {' · '}
              {t('{{n}} runs', { n: totals?.runs ?? runs.length })}
            </span>
          }
        >
          <Button
            variant="outline"
            size="small"
            onClick={refresh}
            disabled={loading}
            isLoading={loading}
            ariaLabel={t('Refresh')}
            icon={<PiArrowsClockwiseLight className="h-4 w-4" />}
          >
            {t('Refresh')}
          </Button>
        </PaneFooter>
      )}
    </>
  );
};

/* ── one run, and the calls under it ──────────────────────────────── */

interface RunRowProps {
  run: AIUsageRun;
  name: string;
  person: any;
  expanded: boolean;
  calls?: ReturnType<typeof useAIUsageRunCalls>['callsByRun'][string];
  callsLoading: boolean;
  onToggle: () => void;
}

const RunRow: React.FC<RunRowProps> = ({
  run, name, person, expanded, calls, callsLoading, onToggle,
}) => {
  const { t } = useTranslation();
  const when = feedTimeLabel(run.startedAt, t);
  const mix = formatModelMix(run.modelMix);

  return (
    <Well className="!p-0 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-start gap-2 p-3 text-start transition-colors
                   duration-200 ease-slide hover:bg-surface-sunken"
      >
        <PiCaretRightLight
          className={`mt-0.5 h-4 w-4 flex-shrink-0 text-fg-muted transition-transform
                      duration-200 ease-slide ${expanded ? 'rotate-90' : ''}`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-sm text-fg-body" title={run.runType}>{name}</span>
            <span className="flex-shrink-0 text-sm font-medium tabular-nums text-fg-strong">
              {t('{{n}} tokens', { n: formatTokensCompact(run.tokens.total) })}
            </span>
          </div>

          {/* Who ran it - a face and a name, never the id behind it. */}
          <div className="mt-1.5 flex items-center gap-2">
            <PersonCell person={person} size="xs" />
            <span className="text-[11px] text-fg-muted" title={when.title}>{when.label}</span>
            {run.status && statusTone(run.status) !== 'gray' && (
              <Badge color={statusTone(run.status)}>{run.status}</Badge>
            )}
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5
                          text-[11px] tabular-nums text-fg-muted">
            <span>{formatDuration(run.durationMs)}</span>
            {run.callCount > 0 && (
              <>
                <span aria-hidden>·</span>
                <span>{t('{{n}} calls', { n: run.callCount })}</span>
              </>
            )}
            {run.docCount > 0 && (
              <>
                <span aria-hidden>·</span>
                <span>{t('{{n}} documents', { n: run.docCount })}</span>
              </>
            )}
            {run.inputBytes > 0 && (
              <>
                <span aria-hidden>·</span>
                <span>{formatBytes(run.inputBytes)}</span>
              </>
            )}
          </div>

          {/* The model mix is a fact about HOW the run was served, so it sits
              under the figures rather than beside the run's name. */}
          {mix && (
            <p className="mt-1 truncate text-[11px] text-fg-muted" title={mix}>{mix}</p>
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-hairline-soft px-3 py-2">
          {callsLoading && !calls ? (
            <Spinner size="sm" centered label={t('Loading calls')} />
          ) : !Array.isArray(calls) || !calls.length ? (
            <p className="py-2 text-center text-[11px] text-fg-muted">
              {t('No call detail for this run')}
            </p>
          ) : (
            <ul className="space-y-1.5">
              {calls.map((call, index) => (
                <li key={call.id || `${call.step}-${index}`} className="flex items-start gap-2">
                  <span
                    className={`mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full ${
                      call.success ? 'bg-champagne' : 'bg-danger-500'
                    }`}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-xs text-fg-body">
                        {call.step || call.deployment || t('Call')}
                      </span>
                      <span className="flex-shrink-0 text-xs tabular-nums text-fg-body">
                        {formatTokensCompact(call.tokens.total)}
                      </span>
                    </div>
                    <p className="truncate text-[11px] tabular-nums text-fg-muted">
                      {call.deployment && call.step ? `${call.deployment} · ` : ''}
                      {t('{{n}} tokens', { n: formatTokensCompact(call.tokens.total) })}
                      {call.latencyMs > 0 ? ` · ${formatDuration(call.latencyMs)}` : ''}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Well>
  );
};

/**
 * The panel, or NOTHING where AI usage is not released yet.
 *
 * A wrapper rather than an early return inside the body, because the body's
 * first act is to mount hooks that fetch - usage, run calls, the org
 * directory - and a hook cannot be skipped once its component renders. The
 * surfaces already drop the pane in a hidden region; this makes any other
 * caller (the `aiUsageDetail` side panel, anything added later) safe by
 * construction. See `config/featureFlags.isAiUsageEnabled`.
 */
const AIUsagePanel: React.FC<AIUsagePanelProps> = (props) =>
  (isAiUsageEnabled() ? <AIUsagePanelBody {...props} /> : null);

export default AIUsagePanel;
